import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import Icon from '../Icon';

function formatDate(dateStr) {
  try {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    if (!day || !month || !year) {
      return dateStr;
    }
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateStr;
  }
}

const StudyItem = ({
  date,
  description,
  numInstances,
  modalities,
  patientName,
  trackedSeries,
  isActive,
  onClick,
}) => {
  const { t } = useTranslation('StudyItem');

  return (
    <div
      className={classnames(
        'flex flex-col transition-colors duration-200 ease-in-out cursor-pointer select-none outline-none border-t first:border-t-0 border-secondary-light/30',
        isActive
          ? 'bg-customblue-30/60 border-l-4 border-l-primary-light shadow-md'
          : 'bg-black hover:bg-secondary-main/40 border-l-4 border-l-transparent'
      )}
      onClick={onClick}
      onKeyDown={onClick}
      role="button"
      tabIndex="0"
    >
      <div className="flex flex-1 flex-col px-4 pb-2.5">
        <div className="flex flex-row items-center justify-between pt-2.5 pb-1.5">
          <div className="text-xs font-medium text-customblue-400">{formatDate(date)}</div>
          <div className="flex flex-row items-center gap-1.5 px-2 py-0.5 rounded-full bg-customblue-30/50 border border-customblue-50/20 text-xs text-primary-light">
            <Icon
              name="group-layers"
              className="w-3.5 h-3.5 text-primary-light"
            />
            <span className="font-semibold">{numInstances}</span>
          </div>
        </div>
        <div
          style={{ overflowWrap: 'break-word' }}
          className="text-sm font-semibold text-white mt-1 text-left tracking-wide"
        >
          {patientName}
        </div>
        <div className="flex flex-row items-center gap-2 mt-2">
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-primary-active/20 text-primary-light border border-primary-active/30 uppercase tracking-wider">
            {modalities}
          </span>
          <span
            className="block text-customblue-400 text-xs text-left flex-1 truncate"
            style={{ lineBreak: 'anywhere' }}
            title={description}
          >
            {description}
          </span>
        </div>
      </div>
      {!!trackedSeries && (
        <div className="flex w-full">
          <div
            className={classnames(
              'flex flex-row items-center justify-center py-1.5 px-3 text-xs font-medium text-white transition-colors duration-200',
              isActive
                ? 'bg-customblue-50/30 border-t border-secondary-light/40 w-full'
                : 'bg-customblue-30/50 mx-4 mb-3 rounded-md border border-customblue-50/20 w-full'
            )}
          >
            <Icon
              name="tracked"
              className="text-primary-light mr-1.5 w-3.5 h-3.5"
            />
            {t('Tracked series', { trackedSeries })}
          </div>
        </div>
      )}
    </div>
  );
};

StudyItem.propTypes = {
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  modalities: PropTypes.string.isRequired,
  numInstances: PropTypes.number.isRequired,
  trackedSeries: PropTypes.number,
  isActive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default StudyItem;
